import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TipCategoryCard from './TipCategoryCard';
import ProTipService from '../../../services/proTipService';

const ProTipsSection = ({ user, onError, isLoading, setIsLoading }) => {
  const [proTips, setProTips] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Tip categories from the database enum
  const tipCategories = [
    { id: 'general', label: 'General', icon: 'Info', description: 'Basic tips for all performers' },
    { id: 'auditioning', label: 'Auditioning', icon: 'Search', description: 'Audition preparation and techniques' },
    { id: 'first_day', label: 'First Day', icon: 'Calendar', description: 'First day of rehearsal guidance' },
    { id: 'during_the_run', label: 'During the Run', icon: 'Play', description: 'Performance and show run tips' },
    { id: 'between_shows', label: 'Between Shows', icon: 'Clock', description: 'Maintaining energy between performances' }
  ];

  // Load pro tips data
  useEffect(() => {
    loadProTips();
  }, []);

  const loadProTips = async () => {
    try {
      setIsLoading?.(true);
      onError?.(null);

      const { data, error } = await ProTipService?.getProTips();

      if (error) {
        onError?.('Failed to load pro tips. Please try again.');
        return;
      }

      setProTips(data || []);
    } catch (err) {
      onError?.('An unexpected error occurred while loading pro tips.');
    } finally {
      setIsLoading?.(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
    }
  };

  const handleTipAdded = async () => {
    // Refresh the pro tips data when a new tip is added
    await loadProTips();
  };

  const handleTipUpdated = async () => {
    // Refresh the pro tips data when a tip is updated
    await loadProTips();
  };

  const handleTipRemoved = async () => {
    // Refresh the pro tips data when a tip is removed
    await loadProTips();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Loading skeleton */}
        {[...Array(5)]?.map((_, index) => (
          <div key={index} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-6 bg-white bg-opacity-20 rounded shimmer w-32"></div>
              <div className="h-4 bg-white bg-opacity-20 rounded shimmer w-16"></div>
            </div>
            <div className="space-y-3">
              {[...Array(2)]?.map((_, tipIndex) => (
                <div key={tipIndex} className="h-4 bg-white bg-opacity-20 rounded shimmer w-full"></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <Icon name="Lightbulb" size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-white">
                Pro Tips Management
              </h2>
              <p className="text-blue-100 text-sm font-body">
                Professional guidance organized by category
              </p>
            </div>
          </div>
          
          <Button
            variant="outline"
            onClick={loadProTips}
            iconName="RefreshCw"
            iconPosition="left"
            disabled={isLoading}
            className="border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-white rounded-lg"
          >
            Refresh Tips
          </Button>
        </div>

        {/* Instructions */}
        <div className="bg-amber-50 bg-opacity-20 border border-amber-200 border-opacity-30 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-amber-200 flex-shrink-0 mt-0.5" />
            <div className="text-amber-100 text-sm font-body">
              <p className="mb-2">
                <strong>How to manage pro tips:</strong>
              </p>
              <ul className="space-y-1 ml-4">
                <li>• Click on a category to expand and view existing tips</li>
                <li>• Add unlimited tips with custom headers and detailed content</li>
                <li>• Drag tips to reorder them within categories</li>
                <li>• Edit or delete tips using the action buttons</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Tip Category Cards */}
      <div className="space-y-4">
        {tipCategories?.map((category) => (
          <TipCategoryCard
            key={category?.id}
            category={category}
            proTips={proTips?.filter(tip => tip?.category === category?.id)}
            isExpanded={expandedCategory === category?.id}
            onToggle={() => handleCategoryToggle(category?.id)}
            onTipAdded={handleTipAdded}
            onTipUpdated={handleTipUpdated}
            onTipRemoved={handleTipRemoved}
            user={user}
            onError={onError}
          />
        ))}
      </div>

      {/* Statistics Summary */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-lg font-heading font-semibold text-white mb-4">
          Pro Tips Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {tipCategories?.map((category) => {
            const tipCount = proTips?.filter(tip => tip?.category === category?.id)?.length || 0;
            return (
              <div key={category?.id} className="text-center">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Icon name={category?.icon} size={20} className="text-white" />
                </div>
                <div className="text-white font-medium text-sm mb-1">{category?.label}</div>
                <div className="text-amber-200 text-xs font-body">{tipCount} tips</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProTipsSection;